import React from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import ReorderableList from './inputs/ReorderableList';

interface BaseInputProps {
    name: string;
    humanReadableName: string;
    description?: string;
    /**
     * If provided, will be used as a tooltip for the input.
     */
    longDescription?: string;
}

interface SelectOption {
    value: string | number;
    label: string;
}

/**
 * Select input dropdown with options
 */
interface SelectInputProps extends BaseInputProps {
    selects: SelectOption[];
}

/**
 * For a "number" text field input
 */
interface NumberInputProps extends BaseInputProps {
    numbers: {
        min?: number;
        max?: number;
        step?: number;
    };
}

interface TextInputProps extends BaseInputProps {
    textFields: {
        maxLength?: number;
        multiline?: boolean;
        type?: 'text' | 'email';
    };
}

/**
 * Boolean input, rendered as a Switch / checkbox
 */
interface BooleanInputProps extends BaseInputProps {
    isBoolean: true;
}

interface ReorderableListInputItem {
    value: string;
    label: string;
}

/**
 * Custom input component for a list whose items can be reordered.
 */
interface ReorderableListInputProps extends BaseInputProps {
    list_items: ReorderableListInputItem[];
}

/**
 * Loops through defined form items and validates the given data against them.
 */
export const validateFormData = (data: any, formItems: FormItem[]): Record<string, string> => {
    const errors: Record<string, string> = {};
    for (const item of formItems) {
        const value = data[item.name];
        if ('numbers' in item) {
            let parsedValue = value;
            if (typeof value !== 'number') {
                parsedValue = parseFloat(value);
            }

            if (isNaN(parsedValue)) {
                console.log(value, parsedValue, item.name);
                errors[item.name] = `Invalid number`;
                continue;
            }

            if (
                (item.numbers.min !== undefined && parsedValue < item.numbers.min) ||
                (item.numbers.max !== undefined && parsedValue > item.numbers.max)
            ) {
                errors[item.name] = `Out of range: ${parsedValue} (must be between  ${item.numbers.min} and ${item.numbers.max})`;
            }
        }
        // For selects and the list, the user cannot mess up, so no validation needed.
        // Famous last words.
    }
    return errors;
};

export type FormItem = NumberInputProps | SelectInputProps | TextInputProps | BooleanInputProps | ReorderableListInputProps;

interface GenericConfigurationFormProps {
    /**
     * Form inputs to render. Defines structure, sets metadata for each input.
     */
    items: FormItem[];
    /**
     * Currently selected values for each input. Keys are the names of the inputs.
     */
    itemValues: Record<string, any>;
    /**
     * Function to update the values of the form inputs. Only a value that
     * changes is passed.
     */
    setItemValues: (values: Record<string, any>) => void;
    /**
     * Map item names to error messages.
     */
    itemErrors: Record<string, string>;
}

/**
 * Renders form elements based on the provided configuration. Parent is
 * expected to manage state.
 */
export const GenericConfigurationForm = ({items, itemValues, setItemValues, itemErrors}: GenericConfigurationFormProps) => {
    const handleChange = (inputType: 'text' | 'number' | 'select' | 'boolean' | 'ordered_list', event: any) => {
        const {name, value, checked} = event.target;
        if (!name) {
            console.warn('Input change event does not have a name attribute:', event);
            return;
        }

        if (inputType === 'ordered_list') {
            // For ordered lists, the value is an array of items
            setItemValues({
                [name]: value.map((item: {id: string; content: string}) => item.id)
            });
            return;
        }

        if (inputType === 'boolean') {
            // For boolean inputs, we use the checked state
            setItemValues({[name]: checked});
            return;
        }

        setItemValues({[name]: value});
    };

    return (
        <>
            {items.map(item => {
                const value = itemValues[item.name] !== undefined ? itemValues[item.name] : '';
                const longDescriptionTooltip = (
                    <Tooltip title={item.longDescription} arrow>
                        <IconButton edge="end" size="small">
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                );
                const helperText = (
                    <>
                        {itemErrors[item.name] ? (
                            <>
                                {itemErrors[item.name]}
                                <br />
                            </>
                        ) : null}
                        {item.description}
                        {item.longDescription ? longDescriptionTooltip : null}
                    </>
                );
                // Handle Select inputs
                if ('selects' in item) {
                    return (
                        <TextField
                            select
                            key={item.name}
                            variant="outlined"
                            name={item.name}
                            value={value}
                            onChange={e => handleChange('select', e)}
                            label={item.humanReadableName}
                            error={Boolean(itemErrors[item.name])}
                            helperText={helperText}>
                            {item.selects.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    );
                }

                // Handle Boolean (Switch) inputs
                if ('isBoolean' in item) {
                    return (
                        <FormControlLabel
                            key={item.name}
                            control={
                                <Switch
                                    name={item.name}
                                    checked={itemValues[item.name] || false}
                                    onChange={e => handleChange('boolean', e)}
                                />
                            }
                            label={item.humanReadableName}
                        />
                    );
                }

                // Handle TextField (text and number) inputs
                if ('textFields' in item || 'numbers' in item) {
                    const isNumber = 'numbers' in item;
                    const inputProps = isNumber
                        ? {
                              min: item.numbers.min,
                              max: item.numbers.max,
                              step: item.numbers.step
                          }
                        : {
                              maxLength: item.textFields.maxLength
                          };

                    return (
                        <TextField
                            key={item.name}
                            label={item.humanReadableName}
                            name={item.name}
                            value={value}
                            onChange={e => handleChange(isNumber ? 'number' : 'text', e)}
                            type={isNumber ? 'number' : item.textFields.type || 'text'}
                            multiline={'textFields' in item && item.textFields.multiline}
                            helperText={helperText}
                            inputProps={inputProps}
                            error={Boolean(itemErrors[item.name])}
                        />
                    );
                }

                if ('list_items' in item) {
                    return (
                        <ReorderableList
                            key={item.name}
                            label={item.humanReadableName}
                            helperText={helperText}
                            initialItems={item.list_items.map(li => {
                                return {id: li.value, content: li.label};
                            })}
                            onOrderChange={new_list => handleChange('ordered_list', {target: {name: item.name, value: new_list}})}
                        />
                    );
                }

                // Fallback for unrecognized types
                return null;
            })}
        </>
    );
};
